import { DecimalPipe } from '@angular/common';
import { Component, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Listbox, ListboxFilterEvent } from 'primeng/listbox';
import { lastValueFrom, map } from 'rxjs';
import {
  ProductControllerService,
  ProductVariantDTO,
  ProductVariantSummary
} from 'src/app/admin-api';
import { AppDialogService } from 'src/app/services/dialog.service';
import { FormUtil } from 'src/app/utils/form.util';
import { CouponProductVariantFormComponent } from '../../coupon-form/coupon-product-variant-form/coupon-product-variant-form.component';

@Component({
  selector: 'app-product-variant-select-modal',
  templateUrl: './product-variant-select-modal.component.html',
  styleUrl: './product-variant-select-modal.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [DecimalPipe]
})
export class ProductVariantSelectModalComponent {
  @ViewChild(Listbox) listBox: Listbox | undefined;

  private ref: DynamicDialogRef = inject(DynamicDialogRef);
  private productService: ProductControllerService = inject(
    ProductControllerService
  );
  private decimalPipe = inject(DecimalPipe);
  private config = inject(DynamicDialogConfig);
  type: number = this.config.data.type || 0;

  product = new FormControl<ProductVariantSummary | ProductVariantDTO>(null, [
    Validators.required
  ]);
  products: Array<
    ProductVariantSummary | (ProductVariantDTO & { disabled: boolean })
  > = [];
  searchValid = false;
  search = this.config.data.search || '';
  loading = false;
  searching = false;
  sorting: 'name' | 'stock' = this.config.data.sorting || 'name';
  buttonLabel = this.config.data.buttonLabel || 'Salvar';
  sortingEnable = this.config.data.sortingEnable === false ? false : true;
  minStock: number = this.config.data.minStock;
  productsToAvoid: number[] = this.config.data.productsToAvoid || [];
  avoidReason: string = this.config.data.avoidReason;

  constructor() {
    if (this.search?.trim().length) {
      this.searchValid = true;
      this.loading = true;
      this.searching = true;
      this.findProducts(this.search).then(() => {
        this.loading = false;
        this.searching = false;
      });
    }
  }

  async submit(): Promise<void> {
    this.ref.close(this.product.value);
  }

  async findProducts(search?: string): Promise<void> {
    if (!search?.trim().length && !this.product?.value) return;
    try {
      const obs =
        this.type > 0
          ? this.productService
              .searchVariantsInStock(search, this.type)
              .pipe(map((data) => data.result))
          : this.productService
              .searchVariantsByNameOrExternalIdOrBrandOrInternalEAN(search)
              .pipe(map((data) => data.result));
      const products =
        (await lastValueFrom(obs)).map((r) => {
          const disabled =
            (this.minStock !== undefined && r.stock < this.minStock) ||
            this.productsToAvoid?.includes(r.productVariantId);
          const productVariantName = `${r.productVariantName} ${
            r.internalEAN ? '[EAN:' + r.internalEAN + ']' : ''
          } ${r.externalId ? '(SKU:' + r.externalId + ')' : ''} - ${r.brandName}
                  ${this.type > 0 ? '(Estoque: ' + this.decimalPipe.transform(r.stock, '1.0-0') + ')' : ''}
                  ${disabled ? ' - ' + (this.minStock !== undefined && r.stock < this.minStock ? 'Estoque mínimo: ' + this.decimalPipe.transform(this.minStock, '1.0-0') : this.avoidReason || 'Indisponível') : ''}`;
          return {
            ...r,
            productVariantName,
            disabled
          };
        }) || [];
      products.sort((p1, p2) => {
        return (
          FormUtil.semAcento(p1.productVariantName)
            .replace("'", '')
            .toLowerCase()
            .localeCompare(
              FormUtil.semAcento(p2.productVariantName)
                .replace("'", '')
                .toLowerCase()
            ) || p1.productVariantId - p2.productVariantId
        );
      });
      this.products = [...products];
      this.changeSorting();
    } catch (error: any) {
      this.products = [];
      AppDialogService.showErrorDialog(error);
    }
  }

  changeSorting(sorting?: 'name' | 'stock') {
    if (sorting) this.sorting = sorting;
    let products = [...this.products];
    products.sort(CouponProductVariantFormComponent.compareNames);
    if (this.sorting === 'stock')
      products = products
        .filter(
          (p) =>
            (p as ProductVariantDTO).stock && (p as ProductVariantDTO).stock > 0
        )
        .concat(
          products.filter(
            (p) =>
              !(p as ProductVariantDTO).stock ||
              (p as ProductVariantDTO).stock <= 0
          )
        );
    this.products = [...products];
  }

  filter($event: ListboxFilterEvent): void {
    this.searchValid = $event.filter?.length > 2;
    this.search = $event.filter;
    if (this.searchValid) {
      this.loading = true;
      setTimeout(async () => {
        if (this.search === $event.filter && !this.searching) {
          this.searching = true;
          await this.findProducts(this.search);
          this.loading = false;
          this.searching = false;
        }
      }, 500);
    } else {
      this.products = [];
    }
  }
}
